import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import ZoeaLogo from '@/components/ZoeaLogo';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { getLandingRoute } from '@/lib/landingRoute';
import './LoginPage.css';

// Keep customer references unpublished until their details are explicitly approved.
const approvedReference = null;

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email || !password) {
      toast.error('Vul alle velden in');
      return;
    }

    setLoading(true);
    try {
      const userData = await login(email, password);
      const nativeRequest = new URLSearchParams(window.location.search).get('native_request');
      if (nativeRequest) {
        window.location.assign(`${process.env.REACT_APP_BACKEND_URL}/api/v1/auth/native/authorize/continue?request_id=${encodeURIComponent(nativeRequest)}`);
        return;
      }
      toast.success('Welkom terug!');
      navigate(getLandingRoute(userData));
    } catch {
      toast.error('Inloggen mislukt');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="zoea-login">
      <section className="zoea-login__form-panel" aria-labelledby="login-title">
        <div className="zoea-login__form-wrap">
          <Link to="/" className="zoea-login__back">
            <ArrowLeft aria-hidden="true" />
            <span>Terug naar Zoea</span>
          </Link>

          <ZoeaLogo className="zoea-login__logo" />

          <header className="zoea-login__intro">
            <p className="zoea-login__eyebrow">Fijn dat je er bent</p>
            <h1 id="login-title">Welkom terug</h1>
            <p>Log in om verder te gaan met je account.</p>
          </header>

          <form onSubmit={handleSubmit} className="zoea-login__form">
            <div className="zoea-login__field">
              <Label htmlFor="email">E-mailadres</Label>
              <Input
                id="email"
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="naam@voorbeeld.nl"
                className="zoea-login__input"
                data-testid="login-email"
              />
            </div>

            <div className="zoea-login__field">
              <div className="zoea-login__password-label">
                <Label htmlFor="password">Wachtwoord</Label>
                <Link to="/forgot-password" className="zoea-login__text-link">Wachtwoord vergeten?</Link>
              </div>
              <div className="zoea-login__password-input">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Je wachtwoord"
                  className="zoea-login__input zoea-login__input--password"
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="zoea-login__visibility"
                  aria-label={showPassword ? 'Wachtwoord verbergen' : 'Wachtwoord tonen'}
                  aria-pressed={showPassword}
                >
                  {showPassword ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="zoea-login__submit"
              data-testid="login-submit"
            >
              {loading ? 'Bezig met inloggen...' : 'Inloggen'}
            </Button>
          </form>

          <p className="zoea-login__register">
            Nog geen account?{' '}
            <Link to="/register" className="zoea-login__text-link">Registreren</Link>
          </p>
        </div>
      </section>

      <aside className="zoea-login__brand-panel" aria-label="Over Zoea">
        <div className="zoea-login__brand-inner">
          <span className="zoea-login__brand-kicker">Digitaal werk- en creatieplatform</span>
          <span className="zoea-login__brand-rule" aria-hidden="true" />
          <h2>Organiseren,<br />verbinden en creëren.</h2>
          <p>Ruimte voor wat mensen samen in beweging brengt.</p>
          {approvedReference && (
            <div className="zoea-login__reference" role="region" aria-label="Klantreferentie">
              {approvedReference.organization && <p>{approvedReference.organization}</p>}
              {approvedReference.context && <span>{approvedReference.context}</span>}
            </div>
          )}
          <div className="zoea-login__orbit" aria-hidden="true">
            <span />
          </div>
        </div>
        <span className="zoea-login__brand-footer">ZOEA <span>·</span> SAMEN RUIMTE MAKEN</span>
      </aside>
    </main>
  );
};

export default LoginPage;
